import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import {
  apiProductDetailByLocationFormatGetCollectionOptions,
  apiProductsIdFormatGetOptions,
} from "@/generated/api/@tanstack/react-query.gen.ts";
import ContainerInset from "@/components/container-inset.tsx";
import { PageErrorNoData } from "@/components/page-error.tsx";
import PageLayoutRoot, {
  PageLayoutContent,
  PageLayoutHeader,
} from "@/components/page-layout.tsx";
import ProductFormSkeleton from "@/app/(authenticated)/$merchantId/products/_component/product-form-skeleton.tsx";
import ProductForm from "@/app/(authenticated)/$merchantId/products/_component/product-form.tsx";
import { useMerchantProvider } from "@/app/(authenticated)/_providers/merchant-provider.tsx";

export const Route = createFileRoute(
  "/(authenticated)/$merchantId/products/$id/"
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { id, merchantId } = Route.useParams();
  const { selectedMerchant } = useMerchantProvider();

  const productQuery = useQuery({
    ...apiProductsIdFormatGetOptions({
      headers: {
        "X-Merchant-Account-Id": selectedMerchant?.merchAccountId ?? "",
      },
      path: { id: id },
    }),
    enabled: !!selectedMerchant?.merchAccountId,
  });

  const productLocationsQuery = useQuery({
    ...apiProductDetailByLocationFormatGetCollectionOptions({
      headers: {
        "X-Merchant-Account-Id": selectedMerchant?.merchAccountId ?? "",
      },
      query: {
        "product.productId": id,
        page: 1,
        itemsPerPage: 99,
      },
    }),
    enabled: !!selectedMerchant?.merchAccountId,
  });

  if (productQuery.isPending || productLocationsQuery.isPending) {
    return (
      <ContainerInset
        breadcrumbs={[
          {
            title: "Products",
            href: `/${merchantId}/products`,
          },
          {
            title: "Product Detail",
            href: "",
          },
        ]}
      >
        <PageLayoutRoot>
          <PageLayoutHeader
            title="Update product"
            description="Update a reusable template for products"
          />
          <PageLayoutContent className="space-y-6">
            <ProductFormSkeleton />
          </PageLayoutContent>
        </PageLayoutRoot>
      </ContainerInset>
    );
  }

  if (productQuery.error || productLocationsQuery.error) {
    return <PageErrorNoData />;
  }

  if (!productQuery.data || !productLocationsQuery.data) {
    return <PageErrorNoData />;
  }

  return (
    <ContainerInset
      breadcrumbs={[
        {
          title: "Products",
          href: `/${merchantId}/products`,
        },
        {
          title: "Product Detail",
          href: "",
        },
      ]}
    >
      <PageLayoutRoot>
        <PageLayoutHeader
          title="Update product"
          description="Update a reusable template for products"
        />
        <PageLayoutContent className="space-y-6">
          <ProductForm
            productId={productQuery.data?.["@id"]}
            initProduct={{
              productIRI: productQuery.data?.["@id"],
              sku: productQuery.data.sku ?? "",
              name: productQuery.data.name ?? "",
              upc: productQuery.data.upc ?? "",
              description: productQuery.data.description ?? undefined,
              category: productQuery.data.category ?? undefined,
              commodityCode: productQuery.data.commodityCode ?? undefined,
              unit: productQuery.data.unit ?? undefined ?? "",
              objectFile: productQuery.data.objectFile ?? undefined,
              productByLocations: productLocationsQuery.data?.member.map(
                (item) => {
                  const getDiscount = () => {
                    if (item.discountType === "A") {
                      return {
                        discountType: item.discountType,
                        discount: item.discount ?? "",
                      };
                    }
                    if (item.discountType === "P") {
                      return {
                        discountType: item.discountType,
                        discountRate: item.discountRate ?? "",
                      };
                    }
                    return { discountType: "N" } as const;
                  };
                  const getInventory = () => {
                    if (item.nonInventoryProduct === false) {
                      return {
                        nonInventoryProduct: item.nonInventoryProduct,
                        alertLevel: item.alertLevel ?? 0,
                        onHand: item.onHand ?? "0",
                        onOrder: item.onOrder ?? "0",
                      };
                    }
                    return { nonInventoryProduct: true } as const;
                  };

                  return {
                    product: productQuery.data?.["@id"] ?? "",
                    productDetailByLocationId:
                      item.productDetailByLocationId ?? "",
                    location: item.location ?? "",
                    cost: item.cost ?? "0",
                    price: item.price ?? "0",
                    currency: item.currency ?? "USD",
                    taxRate: item.taxRate ?? undefined,
                    ...getDiscount(),
                    ...getInventory(),
                  };
                }
              ),
            }}
            onUpdateSuccess={() => {
              productQuery.refetch();
              productLocationsQuery.refetch();
            }}
          />
        </PageLayoutContent>
      </PageLayoutRoot>
    </ContainerInset>
  );
}
